import { KeyRound, Plus, Users } from "lucide-react";
import { useState } from "react";

import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Pill,
  SelectField,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TextField,
} from "@/components/ui";
import { formatRelativeTime } from "@/lib/format";
import {
  settingsRepository,
  type StaffMember,
} from "@/repositories/settingsRepository";
import { useAuthStore } from "@/stores/authStore";

import {
  settingsKeys,
  useBranches,
  useInvalidate,
  useMutation,
  useRoles,
  useSections,
  useStaff,
} from "../useSettingsData";

export function UsersTab() {
  const canManage = useAuthStore((s) => s.can("users.manage"));
  const canResetPin = useAuthStore((s) => s.can("users.reset_pin"));
  const staff = useStaff();
  const [adding, setAdding] = useState(false);

  if (staff.isLoading) return <SkeletonRows rows={5} />;

  return (
    <Card flush className="p-5">
      <CardHeader
        title="Staff"
        subtitle="Who can sign in, and what they are allowed to do."
        action={
          canManage && (
            <Button
              variant="primary"
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() => setAdding(true)}
            >
              Add someone
            </Button>
          )
        }
      />

      {adding && <AddStaffForm onDone={() => setAdding(false)} />}

      {staff.data && staff.data.length > 0 ? (
        <StaffTable
          staff={staff.data}
          canManage={canManage}
          canResetPin={canResetPin}
        />
      ) : (
        !adding && (
          <EmptyState
            icon={Users}
            title="No staff yet"
            description="Add a cashier or manager so they can sign in at a till."
          />
        )
      )}
    </Card>
  );
}

function StaffTable({
  staff,
  canManage,
  canResetPin,
}: {
  staff: StaffMember[];
  canManage: boolean;
  canResetPin: boolean;
}) {
  const invalidate = useInvalidate(settingsKeys.staff);
  const [resetting, setResetting] = useState<StaffMember | null>(null);

  const deactivate = useMutation({
    mutationFn: (id: string) => settingsRepository.deactivateStaff(id),
    onSuccess: invalidate,
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => settingsRepository.reactivateStaff(id),
    onSuccess: invalidate,
  });

  return (
    <>
      {resetting && (
        <ResetPinForm member={resetting} onDone={() => setResetting(null)} />
      )}

      <Table className="mt-3">
        <THead>
          <TH>Name</TH>
          <TH hideBelow="sm">Staff code</TH>
          <TH>Role</TH>
          <TH hideBelow="lg">Sign-in</TH>
          <TH hideBelow="md">Last seen</TH>
          {(canManage || canResetPin) && <TH className="text-right">Actions</TH>}
        </THead>
        <TBody>
          {staff.map((member) => (
            <TR
              key={member.id}
              className={member.is_active ? "" : "opacity-55"}
            >
              <TD className="font-medium">{member.display_name}</TD>
              <TD hideBelow="sm" className="font-numeric">
                {member.staff_code}
              </TD>
              <TD>
                <div className="flex flex-wrap gap-1">
                  {member.assignments.length === 0 ? (
                    // A user with no assignment can sign in and do nothing —
                    // fail-closed, and worth surfacing so it is not mistaken for
                    // a bug.
                    <Pill tone="warn">No role</Pill>
                  ) : (
                    member.assignments.map((a) => (
                      <Pill
                        key={`${a.role_id}-${a.scope_id ?? "all"}`}
                        tone="neutral"
                      >
                        {a.role_name}
                        {a.scope_type !== "MERCHANT" &&
                          ` · ${a.scope_type.toLowerCase()}`}
                      </Pill>
                    ))
                  )}
                </div>
              </TD>
              <TD hideBelow="lg">
                <div className="flex flex-wrap gap-1">
                  {member.email && <Pill tone="neutral">Password</Pill>}
                  {member.has_pin && <Pill tone="success">PIN</Pill>}
                  {member.is_pin_locked && (
                    <Pill tone="danger">PIN locked</Pill>
                  )}
                  {!member.email && !member.has_pin && (
                    <Pill tone="warn">Cannot sign in</Pill>
                  )}
                </div>
              </TD>
              <TD hideBelow="md" className="text-ink-600">
                {formatRelativeTime(member.last_login_at)}
              </TD>
              {(canManage || canResetPin) && (
                <TD className="text-right">
                  <div className="flex justify-end gap-2">
                    {canResetPin && member.is_active && (
                      <Button
                        size="sm"
                        icon={<KeyRound className="size-4" aria-hidden />}
                        onClick={() => setResetting(member)}
                      >
                        {member.is_pin_locked ? "Unlock" : "Set PIN"}
                      </Button>
                    )}
                    {!canManage ? null : member.is_active ? (
                      <Button
                        size="sm"
                        variant="danger"
                        loading={
                          deactivate.isPending &&
                          deactivate.variables === member.id
                        }
                        onClick={() => deactivate.mutate(member.id)}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        loading={
                          reactivate.isPending &&
                          reactivate.variables === member.id
                        }
                        onClick={() => reactivate.mutate(member.id)}
                      >
                        Reactivate
                      </Button>
                    )}
                  </div>
                </TD>
              )}
            </TR>
          ))}
        </TBody>
      </Table>
    </>
  );
}

function ResetPinForm({
  member,
  onDone,
}: {
  member: StaffMember;
  onDone: () => void;
}) {
  const invalidate = useInvalidate(settingsKeys.staff);
  const [pin, setPin] = useState("");

  const reset = useMutation({
    mutationFn: (value: string) =>
      settingsRepository.resetPin(member.id, value),
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });

  return (
    <div className="mt-4 space-y-3 rounded-card bg-canvas p-4">
      <p className="text-sm font-medium text-ink-900">
        Set a PIN for {member.display_name}
      </p>
      <p className="text-xs text-ink-500">
        Four to six digits. Not a run like 1234, and not the same digit
        repeated. Setting a PIN also clears a lockout.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <TextField
          label="New PIN"
          className="w-40"
          inputMode="numeric"
          value={pin}
          onChange={(e) =>
            setPin(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
          error={
            reset.isError
              ? ((reset.error as { detail?: string })?.detail ??
                "That PIN was refused.")
              : undefined
          }
        />
        <Button
          variant="primary"
          loading={reset.isPending}
          disabled={pin.length < 4}
          onClick={() => reset.mutate(pin)}
        >
          Set PIN
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

function AddStaffForm({ onDone }: { onDone: () => void }) {
  const invalidate = useInvalidate(settingsKeys.staff);
  const roles = useRoles();
  const branches = useBranches();
  const sections = useSections();

  const [form, setForm] = useState({
    staff_code: "",
    first_name: "",
    last_name: "",
    email: "",
    pin: "",
    role_id: "",
    scope_type: "MERCHANT",
    scope_id: "",
  });

  const create = useMutation({
    mutationFn: settingsRepository.createStaff,
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });

  const scopeOptions =
    form.scope_type === "BRANCH"
      ? (branches.data ?? []).map((b) => ({ value: b.id, label: b.name }))
      : form.scope_type === "SECTION"
        ? (sections.data ?? []).map((s) => ({
            value: s.id,
            label: `${s.name} — ${s.branch_name}`,
          }))
        : [];

  const fieldError = (name: string): string | undefined => {
    const errors = (create.error as { fieldErrors?: Record<string, string[]> })
      ?.fieldErrors;
    return errors?.[name]?.[0];
  };

  return (
    <div className="mt-4 space-y-4 rounded-card bg-canvas p-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          label="First name"
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          error={fieldError("first_name")}
        />
        <TextField
          label="Surname"
          value={form.last_name}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
        />
        <TextField
          label="Staff code"
          hint="What they type at the till."
          value={form.staff_code}
          onChange={(e) =>
            setForm({ ...form, staff_code: e.target.value.toUpperCase() })
          }
          error={fieldError("staff_code")}
        />
        <TextField
          label="Email (optional)"
          hint="Only needed to sign in to the back office."
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          error={fieldError("email")}
        />
        <TextField
          label="PIN (optional)"
          hint="Four to six digits, for signing in at a till."
          inputMode="numeric"
          value={form.pin}
          onChange={(e) =>
            setForm({
              ...form,
              pin: e.target.value.replace(/\D/g, "").slice(0, 6),
            })
          }
          error={fieldError("pin")}
        />
      </div>

      <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-3">
        <SelectField
          label="Role"
          value={form.role_id}
          onChange={(e) => setForm({ ...form, role_id: e.target.value })}
          options={[
            { value: "", label: "No role yet" },
            ...(roles.data ?? []).map((r) => ({ value: r.id, label: r.name })),
          ]}
        />
        <SelectField
          label="Applies to"
          value={form.scope_type}
          onChange={(e) =>
            setForm({ ...form, scope_type: e.target.value, scope_id: "" })
          }
          options={[
            { value: "MERCHANT", label: "The whole business" },
            { value: "BRANCH", label: "One branch" },
            { value: "SECTION", label: "One section" },
          ]}
        />
        {form.scope_type !== "MERCHANT" && (
          <SelectField
            label={
              form.scope_type === "BRANCH" ? "Which branch" : "Which section"
            }
            value={form.scope_id}
            onChange={(e) => setForm({ ...form, scope_id: e.target.value })}
            options={[{ value: "", label: "Choose one" }, ...scopeOptions]}
          />
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          loading={create.isPending}
          disabled={
            !form.first_name.trim() ||
            !form.staff_code.trim() ||
            (form.scope_type !== "MERCHANT" && !form.scope_id)
          }
          onClick={() =>
            create.mutate({
              staff_code: form.staff_code.trim(),
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              email: form.email.trim() || null,
              ...(form.pin ? { pin: form.pin } : {}),
              ...(form.role_id ? { role_id: form.role_id } : {}),
              scope_type: form.scope_type,
              ...(form.scope_id ? { scope_id: form.scope_id } : {}),
            })
          }
        >
          Add
        </Button>
        <Button onClick={onDone}>Cancel</Button>
        {create.isError &&
          !Object.keys(
            (create.error as { fieldErrors?: object })?.fieldErrors ?? {},
          ).length && (
            <span className="text-sm text-danger-700" role="alert">
              {(create.error as { detail?: string })?.detail ??
                "Could not add this person."}
            </span>
          )}
      </div>
    </div>
  );
}
